import React from "react";

import display from "@iconify/icons-bi/display";
import moon from "@iconify/icons-bi/moon";
import sun from "@iconify/icons-bi/sun";
import { Icon } from "@iconify/react/dist/offline";

import { Button } from "src/components/ui/button";
import useLocalStorage from "src/hooks/useLocalStorage";

import ThemeToggle from "src/components/ui/sidebar/user-menu/ThemeToggle";
import type { ThemeChoice, ThemeSettings } from "src/components/ui/sidebar/user-menu/user-menu-utils";
import { deriveThemeChoice, getSystemThemePreference } from "src/components/ui/sidebar/user-menu/user-menu-utils";

const baseAssetUrl = import.meta.env.VITE_BASE_ASSET_URL || "";

const THEME_CYCLE: ThemeChoice[] = ["light", "dark", "system"];
const THEME_ICONS = { light: sun, dark: moon, system: display } as const;
const THEME_LABELS = { light: "Light", dark: "Dark", system: "System" } as const;

function useThemeSettings() {
  const [settings, setSettings] = useLocalStorage<ThemeSettings>("settings", {
    themeMode: "light",
    themeColor: "light",
    themeDirection: "ltr",
    view: null,
  });

  const themeChoice = deriveThemeChoice(settings);

  const handleThemeChange = (choice: ThemeChoice) => {
    if (choice === "system") {
      const systemPref = getSystemThemePreference();
      setSettings({
        ...settings,
        themeMode: systemPref,
        themeColor: systemPref,
        userSelectedTheme: false,
      });
    } else {
      setSettings({
        ...settings,
        themeMode: choice,
        themeColor: choice,
        userSelectedTheme: true,
      });
    }
  };

  return { themeChoice, handleThemeChange };
}

/** Full segmented control — desktop */
function DesktopThemeToggle() {
  const { themeChoice, handleThemeChange } = useThemeSettings();
  return <ThemeToggle theme={themeChoice} onThemeChange={handleThemeChange} />;
}

/** Small icon button that cycles through modes — mobile */
function MobileThemeIcon() {
  const { themeChoice, handleThemeChange } = useThemeSettings();

  const cycle = () => {
    const idx = THEME_CYCLE.indexOf(themeChoice);
    const next = THEME_CYCLE[(idx + 1) % THEME_CYCLE.length];
    handleThemeChange(next);
  };

  return (
    <button
      onClick={cycle}
      aria-label={`Theme: ${THEME_LABELS[themeChoice]}`}
      className="p-1.5 rounded-md text-primary-foreground/60 hover:text-primary-foreground transition-colors"
    >
      <Icon icon={THEME_ICONS[themeChoice]} width={16} height={16} />
    </button>
  );
}

export default function LoginLayout({
  children,
}: {
  children: React.ReactNode;
}) {
  return (
    <div className="fixed inset-0 flex overflow-hidden">
      {/* Branded left panel — desktop only */}
      <div className="auth-panel hidden lg:flex lg:w-[480px] xl:w-[560px] shrink-0 flex-col justify-between p-10 relative overflow-hidden">
        <div className="absolute inset-0 opacity-[0.04] auth-dot-pattern" />

        {/* Icon logo — partially visible, anchored bottom-right for depth */}
        <img
          src={`${baseAssetUrl}/logo-icon.svg`}
          alt=""
          aria-hidden="true"
          className="auth-panel-img absolute -bottom-16 -right-16 w-[420px] h-[420px] opacity-[0.08]"
        />

        {/* Full wordmark at top */}
        <img
          src={`${baseAssetUrl}/logo-pitchbook.svg`}
          alt={import.meta.env.VITE_COMPANY_NAME}
          width={200}
          className="auth-panel-img relative"
        />

        <div className="relative space-y-4">
          <h1
            className="text-3xl font-semibold leading-tight"
            style={{ color: "var(--auth-fg)" }}
          >
            Portfolio data,
            <br />
            from the source.
          </h1>
          <p
            className="text-base max-w-[340px] opacity-50"
            style={{ color: "var(--auth-fg)" }}
          >
            Automate data collection, track compliance, and report across your
            entire portfolio.
          </p>
        </div>

        <div
          className="relative h-px w-12 opacity-15"
          style={{ backgroundColor: "var(--auth-fg)" }}
        />
      </div>

      {/* Right panel — form area */}
      <div className="flex-1 min-w-0 flex flex-col bg-background overflow-y-auto relative">
        {/* Faint icon watermark — mobile only */}
        <img
          src={`${baseAssetUrl}/logo-icon.svg`}
          alt=""
          aria-hidden="true"
          className="absolute -bottom-10 -right-10 w-[300px] h-[300px] opacity-[0.03] dark:opacity-[0.07] dark:invert lg:hidden"
        />

        {/* Mobile header bar — compact with icon-only theme toggle */}
        <div className="auth-panel flex items-center justify-between px-5 py-3 lg:hidden">
          <img
            src={`${baseAssetUrl}/logo-pitchbook.svg`}
            alt={import.meta.env.VITE_COMPANY_NAME}
            height={20}
            className="auth-panel-img h-5 w-auto"
          />
          <MobileThemeIcon />
        </div>

        {/* Desktop theme toggle — full segmented control */}
        <div className="hidden lg:flex justify-end p-4">
          <DesktopThemeToggle />
        </div>

        <div className="flex-1 flex items-center justify-center px-6">
          <div className="w-[400px] max-w-full pb-16">{children}</div>
        </div>
      </div>
    </div>
  );
}
